import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../config/firebase';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';
import firebaseAuthService from '../../../services/firebase/FirebaseAuthService';
import { useToast } from '../../Toast';
import { UserRole } from '../../../types/roles';
import PracticeRolesService from '../../../services/PracticeRolesService';

// Fallback staff roles (used if PracticeRolesService not initialized)
const DEFAULT_STAFF_ROLES: { value: string; label: string }[] = [
  { value: 'provider', label: 'Provider (Clinician)' },
  { value: 'clinical_director', label: 'Clinical Director' },
  { value: 'administrative_staff', label: 'Administrative Staff' },
  { value: 'hr_admin', label: 'HR Admin' },
  { value: 'system_admin', label: 'System Administrator' },
];

// Utility function to compress images
const compressImage = (file: File, maxWidth: number = 300, quality: number = 0.7): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;

      // Draw and compress
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedDataUrl);
    };

    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const TeamMembers: React.FC = () => {
  const toast = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const teamMemberService = TeamMemberService.getInstance();

  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState<any>(null);
  const [teamMemberSearch, setTeamMemberSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('All roles');
  const [showTeamMemberActions, setShowTeamMemberActions] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{show: boolean; memberId: string; memberName: string} | null>(null);

  // Photo preview states
  const [editMemberPhotoPreview, setEditMemberPhotoPreview] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<string>('');
  const [practiceName, setPracticeName] = useState<string>('');
  const [staffRoles, setStaffRoles] = useState<{ value: string; label: string }[]>(DEFAULT_STAFF_ROLES);

  // Fetch practice name from current user's provider profile
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, 'providers', uid)).then(snap => {
      if (snap.exists() && snap.data().practiceName) {
        setPracticeName(snap.data().practiceName);
      }
    }).catch(() => {});
  }, []);

  // Load dynamic roles from PracticeRolesService
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const rolesService = PracticeRolesService.getInstance();
    rolesService.initialize(uid);
    const handleUpdate = () => {
      const opts = rolesService.getRoleOptions();
      if (opts.length > 0) setStaffRoles(opts);
    };
    rolesService.subscribe(handleUpdate);
    handleUpdate();
    return () => rolesService.unsubscribe(handleUpdate);
  }, []);

  const isClinicalRole = (role: string) => {
    const r = role.toLowerCase();
    return r.includes('provider') || r.includes('clinician') || r.includes('director');
  };

  useEffect(() => {
    setTeamMembers(teamMemberService.getAllTeamMembers());

    // Subscribe to real-time Firestore updates
    const handleUpdate = () => {
      setTeamMembers(teamMemberService.getAllTeamMembers());
    };
    teamMemberService.subscribe(handleUpdate);

    // Add click outside handler to close team member actions
    const handleClickOutside = (event: MouseEvent) => {
      if (showTeamMemberActions) {
        const target = event.target as Element;
        const dropdownElement = document.querySelector('[data-dropdown="team-actions"]');
        const buttonElement = document.querySelector('[data-button="team-actions"]');

        if (dropdownElement && !dropdownElement.contains(target) &&
            buttonElement && !buttonElement.contains(target)) {
          setShowTeamMemberActions(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      teamMemberService.unsubscribe(handleUpdate);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTeamMemberActions, teamMemberService]);

  // Team Member Functions - Invite flow (name, email, role only)
  const inviteTeamMember = async (memberData: { name: string; email: string; role: string }) => {
    const newMember: TeamMember = {
      id: '',
      name: memberData.name,
      email: memberData.email,
      role: memberData.role,
      twoFactor: false,
      lastSignIn: 'Never',
      education: [],
      certifications: [],
      specialties: [],
      languages: [],
      availability: '',
      bio: '',
      location: '',
      phone: '',
      insuranceAccepted: [],
      ratings: 0,
      reviews: 0,
      initials: memberData.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || '',
      avatarColor: `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`,
      status: 'invited',
      invitedAt: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    };

    try {
      await teamMemberService.addTeamMember(newMember);
      setShowAddTeamMemberModal(false);
    } catch (err: any) {
      console.error('Failed to add team member:', err);
      toast.error('Error', `Failed to save team member: ${err.message || 'Unknown error'}`);
      return;
    }

    // Send invite via Firebase email link (free, works with any email)
    firebaseAuthService.sendInviteLink({
      email: memberData.email,
      role: memberData.role,
      name: memberData.name,
      practiceName: practiceName || undefined,
    }).then(sent => {
      if (sent) {
        toast.success('Invite Sent', `Invitation email sent to ${memberData.email}`);
      } else {
        toast.success('Member Added', `${memberData.name} added. Email invite could not be sent — share the signup link manually.`);
      }
    }).catch(() => {
      toast.success('Member Added', `${memberData.name} added. Email invite could not be sent — share the signup link manually.`);
    });
  };

  const saveEditedMember = async (id: string, updatedData: any) => {
    try {
      await teamMemberService.updateTeamMember(id, updatedData);
      setEditingTeamMember(null);
      setEditMemberPhotoPreview(null);
      toast.success('Member Updated', 'Team member has been updated successfully.');
    } catch (err: any) {
      console.error('Failed to update team member:', err);
      toast.error('Error', `Failed to update: ${err.message || 'Unknown error'}`);
    }
  };

  const deleteTeamMember = async (id: string) => {
    try {
      await teamMemberService.deleteTeamMember(id);
      setShowTeamMemberActions(null);
    } catch (err: any) {
      console.error('Failed to delete team member:', err);
      toast.error('Error', `Failed to delete: ${err.message || 'Unknown error'}`);
    }
  };

  const handleEditMemberPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditMemberPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredTeamMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(teamMemberSearch.toLowerCase()) ||
                         member.email.toLowerCase().includes(teamMemberSearch.toLowerCase());
    const matchesRole = selectedRole === 'All roles' || member.role.includes(selectedRole);
    return matchesSearch && matchesRole;
  });

  return (
    <div style={{ padding: '32px', maxWidth: '1400px' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
            Team Members
          </h1>
          <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
            Manage your practice team members and their access
          </p>
        </div>
        <button
          onClick={() => setShowAddTeamMemberModal(true)}
          style={{
            padding: '10px 24px',
            backgroundColor: '#226F9E',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          Invite Team Member
        </button>
      </div>

      {/* Search and Filter */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={teamMemberSearch}
          onChange={(e) => setTeamMemberSearch(e.target.value)}
          style={{
            flex: '1',
            minWidth: '200px',
            padding: '12px',
            borderRadius: '6px',
            border: '1px solid #6CA6C1',
            backgroundColor: '#113F67',
            color: '#FFFFFF',
            fontSize: '14px'
          }}
        />
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          style={{
            padding: '12px',
            borderRadius: '6px',
            border: '1px solid #6CA6C1',
            backgroundColor: '#113F67',
            color: '#FFFFFF',
            fontSize: '14px',
            minWidth: '150px'
          }}
        >
          <option>All roles</option>
          {staffRoles.map(r => (
            <option key={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      {/* Team Members Table */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        border: '1px solid #6CA6C1',
        overflow: 'visible'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 2fr 1fr 1fr auto',
          gap: '16px',
          padding: '16px 20px',
          backgroundColor: '#1f2937',
          borderBottom: '1px solid #6CA6C1',
          fontSize: '14px',
          fontWeight: '600',
          color: '#d1d5db'
        }}>
          <div>Name</div>
          <div>Role</div>
          <div>Status</div>
          <div>Last sign in</div>
          <div style={{ width: '20px' }}></div>
        </div>

        {/* Team Member Rows */}
        {filteredTeamMembers.map((member) => (
          <div key={member.id} style={{
            display: 'grid',
            gridTemplateColumns: '2fr 2fr 1fr 1fr auto',
            gap: '16px',
            padding: '16px 20px',
            alignItems: 'center',
            borderBottom: '1px solid #374151'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: member.avatarColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                {member.initials}
              </div>
              <div>
                <div style={{ color: '#3b82f6', fontSize: '14px', fontWeight: '500' }}>
                  {member.name}
                </div>
                <div style={{ color: '#d1d5db', fontSize: '12px' }}>
                  {member.email}
                </div>
              </div>
            </div>
            <div style={{ color: '#f9fafb', fontSize: '14px' }}>
              {member.role}
            </div>
            <div>
              {member.status === 'invited' ? (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: 'rgba(234,179,8,0.15)',
                  color: '#FBBF24'
                }}>
                  Invited
                </span>
              ) : (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: 'rgba(16,185,129,0.15)',
                  color: '#6EE7B7'
                }}>
                  Active
                </span>
              )}
            </div>
            <div style={{ color: '#f9fafb', fontSize: '14px' }}>
              {member.lastSignIn}
            </div>
            <div style={{ position: 'relative' }}>
              <button
                data-button="team-actions"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTeamMemberActions(showTeamMemberActions === member.id ? null : member.id);
                }}
                style={{
                  background: '#4B5563',
                  border: '1px solid #6B7280',
                  color: '#F9FAFB',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  minWidth: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#374151';
                  e.currentTarget.style.borderColor = '#9CA3AF';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#4B5563';
                  e.currentTarget.style.borderColor = '#6B7280';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ⋯
              </button>
              {showTeamMemberActions === member.id && (
                <div
                  data-dropdown="team-actions"
                  style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: '-10px',
                  backgroundColor: '#1F2937',
                  border: '3px solid #6CA6C1',
                  borderRadius: '10px',
                  padding: '16px 12px',
                  minWidth: '220px',
                  maxWidth: '300px',
                  zIndex: 9999,
                  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.9), 0 0 0 2px rgba(212, 160, 23, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                  overflow: 'visible'
                }}>
                  <div style={{
                    color: '#D4A017',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    padding: '4px 8px',
                    textAlign: 'center'
                  }}>
                    TEAM MEMBER ACTIONS
                  </div>

                  <button
                    onClick={() => {
                      setEditingTeamMember(member);
                      setEditRole(member.role);
                      setShowTeamMemberActions(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: '#28527A',
                      border: '1px solid #6CA6C1',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginBottom: '8px'
                    }}
                  >
                    Edit Member
                  </button>

                  <button
                    onClick={() => {
                      setDeleteConfirmation({
                        show: true,
                        memberId: member.id,
                        memberName: member.name
                      });
                      setShowTeamMemberActions(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: '#DC2626',
                      border: '1px solid #EF4444',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Delete Member
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {filteredTeamMembers.length === 0 && (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#d1d5db',
            fontSize: '14px'
          }}>
            No team members found matching your search.
          </div>
        )}
      </div>

      {/* Invite Team Member Modal */}
      {showAddTeamMemberModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#28527A',
            borderRadius: '8px',
            padding: '24px',
            width: '450px',
            maxWidth: '90vw',
            border: '1px solid #6CA6C1'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#F7E9A0', margin: '0 0 8px 0' }}>
              Invite Team Member
            </h2>
            <p style={{ fontSize: '13px', color: '#9CA3AF', margin: '0 0 20px 0' }}>
              Send an invitation for them to complete their profile.
            </p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              await inviteTeamMember({
                name: formData.get('name') as string,
                email: formData.get('email') as string,
                role: formData.get('role') as string
              });
            }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Full Name *
                </label>
                <input name="name" type="text" required placeholder="Enter full name" style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Email Address *
                </label>
                <input name="email" type="email" required placeholder="Enter email address" style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Role *
                </label>
                <select name="role" required style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', boxSizing: 'border-box'
                }}>
                  <option value="">Select a role</option>
                  {staffRoles.map(r => (
                    <option key={r.value} value={r.label}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddTeamMemberModal(false)} style={{
                  padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #6CA6C1',
                  borderRadius: '6px', color: '#f9fafb', fontSize: '14px', cursor: 'pointer'
                }}>
                  Cancel
                </button>
                <button type="submit" style={{
                  padding: '10px 20px', backgroundColor: '#3b82f6', border: 'none',
                  borderRadius: '6px', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}>
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Team Member Modal */}
      {editingTeamMember && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 1000,
          overflowY: 'auto',
          padding: '20px 0'
        }}>
          <div style={{
            backgroundColor: '#28527A',
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: 'calc(100vh - 40px)',
            overflowY: 'auto',
            margin: 'auto',
            border: '1px solid #6CA6C1'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#F7E9A0', margin: '0 0 20px 0' }}>
              Edit Team Member
            </h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);

              const photoFile = formData.get('photo') as File;
              const processUpdate = (photoUrl?: string) => {
                saveEditedMember(editingTeamMember.id, {
                  name: formData.get('name') as string,
                  email: formData.get('email') as string,
                  role: formData.get('role') as string,
                  bio: formData.get('bio') as string,
                  availability: formData.get('availability') as string,
                  education: (formData.get('education') as string || '').split('\n').filter((s: string) => s.trim()),
                  certifications: (formData.get('certifications') as string || '').split('\n').filter((s: string) => s.trim()),
                  specialties: (formData.get('specialties') as string || '').split('\n').filter((s: string) => s.trim()),
                  initials: (formData.get('name') as string)?.split(' ').map((n: string) => n[0]).join('').toUpperCase(),
                  ...(photoUrl ? { photoUrl } : {}),
                  status: 'active'
                });
              };

              if (photoFile && photoFile.size > 0) {
                compressImage(photoFile, 300, 0.7).then(processUpdate).catch(() => {
                  toast.warning('Image Error', 'Error processing image.');
                });
              } else {
                processUpdate();
              }
            }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Full Name *
                </label>
                <input name="name" type="text" required defaultValue={editingTeamMember.name} style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Email Address *
                </label>
                <input name="email" type="email" required defaultValue={editingTeamMember.email} style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Role *
                </label>
                <select name="role" required defaultValue={editingTeamMember.role}
                  onChange={(e) => setEditRole(e.target.value)}
                  style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', boxSizing: 'border-box'
                }}>
                  {staffRoles.map(r => (
                    <option key={r.value} value={r.label}>{r.label}</option>
                  ))}
                </select>
              </div>

              {/* Clinical profile fields - only for Providers and Clinical Directors */}
              {isClinicalRole(editRole || editingTeamMember.role) && (<>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Profile Photo
                </label>
                <div style={{
                  border: '2px dashed #6CA6C1', borderRadius: '8px', padding: '20px',
                  textAlign: 'center', backgroundColor: '#113F67'
                }}>
                  {(editMemberPhotoPreview || editingTeamMember.photoUrl) && (
                    <div style={{ marginBottom: '12px' }}>
                      <img src={editMemberPhotoPreview || editingTeamMember.photoUrl} alt="Profile"
                        style={{ maxHeight: '80px', maxWidth: '80px', objectFit: 'cover', borderRadius: '50%', border: '2px solid #6CA6C1' }} />
                    </div>
                  )}
                  <input name="photo" type="file" id="editMemberPhoto" accept="image/*"
                    onChange={handleEditMemberPhotoChange} style={{ display: 'none' }} />
                  <label htmlFor="editMemberPhoto" style={{
                    color: '#3b82f6', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline'
                  }}>
                    {editingTeamMember.photoUrl ? 'Change photo' : 'Upload photo'}
                  </label>
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Professional Bio
                </label>
                <textarea name="bio" rows={3} defaultValue={editingTeamMember.bio || ''} placeholder="Professional bio..." style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Education (one per line)
                </label>
                <textarea name="education" rows={2}
                  defaultValue={Array.isArray(editingTeamMember.education) ? editingTeamMember.education.join('\n') : ''}
                  placeholder="Degree, University" style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Certifications (one per line)
                </label>
                <textarea name="certifications" rows={2}
                  defaultValue={Array.isArray(editingTeamMember.certifications) ? editingTeamMember.certifications.join('\n') : ''}
                  placeholder="LCSW, EMDR, etc." style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Specialties (one per line)
                </label>
                <textarea name="specialties" rows={2}
                  defaultValue={Array.isArray(editingTeamMember.specialties) ? editingTeamMember.specialties.join('\n') : ''}
                  placeholder="Anxiety, Depression, etc." style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box'
                }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#e5e7eb', marginBottom: '8px' }}>
                  Availability
                </label>
                <input name="availability" type="text" defaultValue={editingTeamMember.availability || ''}
                  placeholder="Monday - Friday, 9:00 AM - 5:00 PM" style={{
                  width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #6CA6C1',
                  backgroundColor: '#113F67', color: '#f9fafb', fontSize: '14px', boxSizing: 'border-box'
                }} />
              </div>
              </>)}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setEditingTeamMember(null); setEditMemberPhotoPreview(null); }} style={{
                  padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #6CA6C1',
                  borderRadius: '6px', color: '#f9fafb', fontSize: '14px', cursor: 'pointer'
                }}>
                  Cancel
                </button>
                <button type="submit" style={{
                  padding: '10px 20px', backgroundColor: '#3b82f6', border: 'none',
                  borderRadius: '6px', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation?.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000
        }}>
          <div style={{
            backgroundColor: '#1F2937',
            borderRadius: '12px',
            border: '2px solid #6CA6C1',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.8)',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#F9FAFB',
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '16px',
              margin: '0 0 16px 0'
            }}>
              Confirm Deletion
            </h3>

            <p style={{
              color: '#D1D5DB',
              fontSize: '16px',
              marginBottom: '24px',
              lineHeight: '1.5',
              margin: '0 0 24px 0'
            }}>
              Are you sure you want to delete team member <strong style={{color: '#F9FAFB'}}>{deleteConfirmation.memberName}</strong>? This action cannot be undone.
            </p>

            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setDeleteConfirmation(null)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#374151',
                  border: '1px solid #6B7280',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minWidth: '100px'
                }}
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  deleteTeamMember(deleteConfirmation.memberId);
                  setDeleteConfirmation(null);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#DC2626',
                  border: '1px solid #EF4444',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minWidth: '100px'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMembers;
